import React from "react";
import { createStyles, makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { Chip } from "./Common";

const useStyles = makeStyles((theme) =>
  createStyles({
    TileBox: {
      alignItems: "center",
      height: 100,
      display: "grid",
      width: "100%",
      textAlign: "center",
      position: "relative",
      borderRadius: 12,
      padding: "28px 12px 12px 12px",
      cursor: "pointer",
    },
    TileProgress: {
      position: "absolute",
      right: 5,
      bottom: 5,
      display: "flex",
    },
    BoxGrid: {
      display: "grid",
      alignItems: "center",
      justifyItems: "center",
      height: "100%"
    },
    BoxTypography: {
      height: "100%",
      "& .Label": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      },
      "& h3": {
        color: "#181C32",
        fontSize: "22px",
        fontWeight: 800,
        margin: "0 0 10px"
      },
      "& h5": {
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
      },
    },
    blockquoteContent: {
      width: "100%",
      display: "grid",
      overflow: "hidden",
      borderRadius: "10px",
      padding: "15px 20px 20px 20px",
      height: "100%",
    },
    captionBox: {
      margin: "10px 0",
    },
    BigTileTypography: {
      display: "grid",
      justifyItems: "left",
      position: "relative",
      alignContent: "start",
      wordBreak: "break-word",
      "& P": {
        lineHeight: "1.50",
        marginBottom: 0,
      },
      "& .MuiBox-root .MuiTypography-caption": {
        lineHeight: "1.66",
        display: "block",
      },
      "& .TopIcon IMG, .BottomIcon IMG": {
        height: "20px",
      },
      "& .BottomIcon": {
        opacity: "0.15",
        marginLeft: "10px",
      },
    },
    IconContainer: {
      justifyItems: "right",
    },
    Formtile: {
      "& .TopIcon": {
        height: "20px",
        justifySelf: "end",
      },
    },
  })
);

const SmallTile = React.memo((configData) => {
  const classes = useStyles();

  return (
    <Box>
      <Paper
        elevation={1}
        className={classes.TileBox}
        onClick={() => configData.clickEvent(configData.link)}
        style={{
          backgroundColor: configData.bgcolor,
          color: configData.color,
          boxShadow: "none"
        }}
      >
        <Grid container direction="row" alignItems={"stretch"} height="100%">
          {configData.label || configData.value ? (
            <Grid item xs={12} className={classes.BoxTypography}>
              <Typography variant="h3">
                {configData.loading ? (
                  <Skeleton variant="rectangular" width={20} height={30} sx={{ borderRadius: 1 }} />
                ) : (
                  configData.value
                )}
              </Typography>
              <Typography variant="h5" textTransform="capitalize">
                {configData.icon}
                <span style={{ marginLeft: "5px" }}> {configData.label} </span>
              </Typography>
            </Grid>
          ) : (
            ""
          )}
        </Grid>
        <div className={classes.TileProgress}>{configData.process ? configData.process : ""}</div>
      </Paper>
    </Box>
  );
});

const TextComponent = ({ label, value, paragraph = false, style = {} }) => {
  return (
    <Typography variant="caption" textTransform="none" paragraph={paragraph} style={style}>
      {label ? <strong>{label}: </strong> : ""}
      {value}
    </Typography>
  );
};

const BigTile = ({ configData, style }) => {
  const classes = useStyles();

  return (
    <blockquote style={{ margin: 0, display: "contents" }}>
      <Paper
        elevation={1}
        className={classes.blockquoteContent}
        sx={{
          borderLeft: configData.borderLeft || "",
          bgcolor: configData.bgcolor || "",
          cursor: configData.cursor || "default",
        }}
        style={{ ...style }}
        onClick={configData.clickEvent ? (e) => configData.clickEvent(configData.link, configData.linkParams ? configData.linkParams : {}) : undefined}
      >
        <Grid container direction="row" height={"100%"}>
          <Grid container direction="row" height={"fit-content"}>
            <Grid
              item
              xs={configData.editIcon || configData.deleteIcon ? 10 : 12}
              className={classes.BigTileTypography}
            >
              <Typography variant="h6" textTransform="none">
                {configData.label}
              </Typography>
            </Grid>
            {configData.deleteIcon ? (
              <Grid item xs={1} className={`${classes.BigTileTypography} ${classes.IconContainer}`}>
                <Box className={"TopIcon"}>{configData.deleteIcon}</Box>
              </Grid>
            ) : (
              ""
            )}
            {configData.editIcon ? (
              <Grid item xs={1} className={`${classes.BigTileTypography} ${classes.IconContainer}`}>
                <Box className={"TopIcon"}>{configData.editIcon}</Box>
              </Grid>
            ) : (
              ""
            )}
          </Grid>

          <Grid item xs={12} className={classes.BigTileTypography} alignItems={"end"}>
            {configData.description ? (
              <Box component="div" overflow="hidden" textOverflow="ellipsis" maxHeight={37}>
                {configData.description ? configData.description : ""}
              </Box>
            ) : (
              ""
            )}
            {
              configData.createCaptionBox ? (
                <Box className={classes.captionBox}>
                  {configData.status ? (
                    <Chip
                      label={configData.status}
                      textcolor={configData.chipTextColor}
                      bgcolor={configData.chipBGColor}
                      size="small"
                      variant="filled"
                    />
                  ) : (
                    ""
                  )}
                  {configData.location ? configData.location : ""}
                  {configData.totalFields ? configData.totalFields : ""}
                  {configData.createdBy ? configData.createdBy : ""}
                </Box>
              ) : ("")
            }
          </Grid>

          {configData.icon || configData.chip ? (
            <Grid container direction="row" alignItems={"end"}>
              <Grid item xs={configData.icon ? 11 : 12} className={classes.BigTileTypography}>
                {Object.keys(configData).length > 0 && configData.chip ? (
                  <Chip
                    label={configData.fileType}
                    sx={{ textTransform: configData.textTransform }}
                    textcolor={configData.chipTextColor}
                    bgcolor={configData.chipBGColor}
                    size="small"
                    variant="filled"
                  />
                ) : (
                  ""
                )}
              </Grid>
              {configData.icon ? (
                <Grid item xs={1} className={classes.BigTileTypography}>
                  <Box className={"BottomIcon"}>{configData.icon}</Box>
                </Grid>
              ) : (
                ""
              )}
            </Grid>
          ) : (
            ""
          )}

          {configData.buttonDisplay ? (
            <Grid container direction="row" alignItems={"end"}>
              <Grid item xs={12} className={classes.BigTileTypography} sx={{ justifyItems: "center !important" }}>
                {configData.button ? configData.button : ""}
              </Grid>
            </Grid>
          ) : (
            ""
          )}
        </Grid>
      </Paper>
    </blockquote>
  );
};

const FormTile = ({ configData, style }) => {
  const classes = useStyles();

  return (
    <blockquote className={classes.Formtile} style={{ margin: 0, display: "contents" }}>
      <Paper
        elevation={1}
        className={classes.blockquoteContent}
        style={{ ...style }}
        sx={{ borderLeft: configData.borderLeft }}
      >
        <Grid container direction="row" height={"fit-content"}>
          <Grid item xs={configData.editIcon ? 11 : 12} className={classes.BigTileTypography} alignItems={"end"}>
            {configData.description ? (
              <Box component="div" sx={{ width: "100%" }}>
                {configData.description}
              </Box>
            ) : (
              ""
            )}
          </Grid>
          <Grid item xs={configData.editIcon ? 1 : 0} className={classes.BigTileTypography}>
            <Typography variant="h6" textTransform="none">
              {configData.label}
            </Typography>
            {configData.editIcon ? <Box className={"TopIcon"}>{configData.editIcon}</Box> : ""}
          </Grid>
        </Grid>
      </Paper>
    </blockquote>
  );
};

export { SmallTile, BigTile, FormTile, TextComponent };
